import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { plansAPI } from '../lib/api';
import PlanCard from '../components/plans/PlanCard';
import { House } from '@phosphor-icons/react';
import { logger } from '../lib/logger';

const CategoryPage = ({ category }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Update document title and meta description
    document.title = category.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', category.metaDescription);
    
    fetchPlans();
    // Reset on unmount
    return () => { document.title = 'UDS Plans | UmPhakathi Design Studio'; };
  }, [category]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      // Convert category filters to API query params
      const params = {};
      Object.entries(category.filters || {}).forEach(([k, v]) => {
        if (k === 'style') {
          // style filter applied client-side
          return;
        }
        params[k] = v;
      });
      
      const response = await plansAPI.getPlans(params);
      let data = response.data;
      
      // Client-side filters
      if (category.filters?.style) {
        data = data.filter(p => (p.style || '').toLowerCase() === category.filters.style.toLowerCase());
      }
      if (category.filters?.max_stand) {
        data = data.filter(p => (p.min_stand_size_sqm || 0) <= category.filters.max_stand);
      }
      
      setPlans(data);
    } catch (err) {
      logger.error('Failed to fetch plans:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-12" data-testid={`category-page-${category.slug}`}>
      <div className="container mx-auto px-4">
        <nav className="text-xs text-text_muted mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          <Link to="/" className="hover:text-accent">HOME</Link>
          <span className="mx-2">/</span>
          <Link to="/plans" className="hover:text-accent">HOUSE PLANS</Link>
          <span className="mx-2">/</span>
          <span className="text-primary">{category.heading.toUpperCase()}</span>
        </nav>

        <div className="max-w-3xl mb-8">
          <div className="flex items-center space-x-3 mb-3">
            <House size={32} weight="duotone" className="text-accent" />
            <h1 className="text-4xl md:text-5xl font-black text-primary tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {category.heading}
            </h1>
          </div>
          <p className="text-text_muted leading-relaxed">{category.intro}</p>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : plans.length === 0 ? (
          <div className="text-center py-20 bg-white border border-border">
            <p className="text-text_muted mb-4">No plans available in this category yet.</p>
            <Link to="/plans" className="inline-block bg-primary text-white px-6 py-3 rounded font-medium hover:bg-opacity-90">
              Browse all plans
            </Link>
          </div>
        ) : (
          <>
            <div className="text-sm text-text_muted mb-4" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              Showing {plans.length} {plans.length === 1 ? 'plan' : 'plans'}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {plans.map(plan => (
                <PlanCard key={plan.plan_id} plan={plan} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;
